import { asBlockedReason, isTerminalStatus, } from "../../services/portal/types";
export function resolveStatusPill(appointment) {
    const status = (appointment.status ?? "").toUpperCase();
    if (appointment.isLive === true) {
        return { labelKey: "statusLiveNow", tone: "success" };
    }
    switch (status) {
        case "SCHEDULED":
            return { labelKey: "statusScheduled", tone: "success" };
        case "CONFIRMED":
            return { labelKey: "statusConfirmed", tone: "success" };
        case "IN_PROGRESS":
            return { labelKey: "statusInProgress", tone: "warning" };
        case "COMPLETED":
            return { labelKey: "statusCompleted", tone: "neutral" };
        case "CANCELLED":
            return { labelKey: "statusCancelled", tone: "error" };
        case "NO_SHOW":
            return { labelKey: "statusNoShow", tone: "neutral" };
        case "MISSED":
            return { labelKey: "statusMissed", tone: "neutral" };
        case "SKIPPED":
            return { labelKey: "statusSkipped", tone: "neutral" };
        default:
            return { labelKey: "statusScheduled", tone: "neutral" };
    }
}
export function canCancelAppointment(appointment, now = Date.now()) {
    if (hasEnded(appointment, now))
        return false;
    return appointment.canCancel ?? !isTerminalStatus(appointment.status);
}
export function hasEnded(appointment, now = Date.now()) {
    return appointment.endEpochMs != null && appointment.endEpochMs < now;
}
export function canRescheduleAppointment(appointment) {
    return appointment.canReschedule ?? !isTerminalStatus(appointment.status);
}
export function canModify(appointment) {
    return canCancelAppointment(appointment) || canRescheduleAppointment(appointment);
}
export function actionBlockedReasonKey(reason) {
    const known = asBlockedReason(reason);
    return known ? `blocked${pascal(known)}` : null;
}
function pascal(code) {
    return code
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("");
}
export function canSelfAttest(appointment, now = Date.now()) {
    if (isTerminalStatus(appointment.status))
        return false;
    return appointment.endEpochMs != null && appointment.endEpochMs < now;
}
export function canClaimPayment(appointment, hasManualMethods) {
    if (!hasManualMethods)
        return false;
    if (appointment.bookedViaSessionPack)
        return false;
    if (appointment.paymentClaimedAt)
        return false;
    const paymentStatus = (appointment.paymentStatus ?? "").toUpperCase();
    if (["PAID", "PAYMENT_PENDING_VERIFICATION", "REFUNDED"].includes(paymentStatus)) {
        return false;
    }
    const due = appointment.paymentAmountDue ?? appointment.consultationCharge ?? 0;
    return due > 0;
}
export function isAwaitingPaymentVerification(appointment) {
    return Boolean(appointment.paymentClaimedAt) && !appointment.paymentVerifiedAt;
}
export function formatMoney(amount, currency) {
    if (amount == null || amount === "")
        return "";
    const value = typeof amount === "string" ? Number(amount) : amount;
    if (!Number.isFinite(value))
        return "";
    if (!currency)
        return String(value);
    try {
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency,
            maximumFractionDigits: 2,
        }).format(value);
    }
    catch {
        return `${currency} ${value}`;
    }
}
export function formatAppointmentDate(appointment) {
    const raw = appointment.fromDateTimeIso ?? appointment.appointmentDate;
    if (!raw)
        return "";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime()))
        return String(raw);
    return date.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
function timeOfDay(iso) {
    if (!iso)
        return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return "";
    return date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
    });
}
export function formatAppointmentTime(appointment) {
    const start = timeOfDay(appointment.fromDateTimeIso);
    const end = timeOfDay(appointment.toDateTimeIso);
    if (start && end)
        return `${start} – ${end}`;
    return start || null;
}
export function formatAppointmentDateParts(appointment, now = new Date()) {
    const raw = appointment.fromDateTimeIso ?? appointment.appointmentDate;
    if (!raw)
        return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime()))
        return null;
    const dayIndex = (value) => Math.floor(new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime() / 86400000);
    const delta = dayIndex(date) - dayIndex(now);
    return {
        weekday: date.toLocaleDateString(undefined, { weekday: "short" }),
        day: date.toLocaleDateString(undefined, { day: "numeric" }),
        month: date.toLocaleDateString(undefined, { month: "short" }),
        relativeKey: delta === 0 ? "today" : delta === 1 ? "tomorrow" : null,
    };
}
